import React, { useState } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { ThemeProvider, alpha } from '@mui/material/styles';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ErrorNotification from '../ErrorNotification/ErrorNotification';
import { useNotifier } from "muinotify";
import { theme1 } from '../../theme/index4';

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required'),
});

const Login = ({ setIsAuthenticated }) => {
  const { notifier } = useNotifier();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validate = () => {
    const values = { email, password };
    validationSchema
      .validate(values, { abortEarly: false })
      .then(() => {
        setErrors({});
        handleSubmit();
      })
      .catch((err) => {
        const newErrors = err.inner.reduce((acc, error) => {
          acc[error.path] = error.message;
          return acc;
        }, {});
        setErrors(newErrors);
      });
  };

  const handleSubmit = () => {
    api.post('/auth/login', { email, password })
      .then((data) => {
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          setIsAuthenticated(true);
          navigate("/dashboard");
        }
      })
      .catch((error) => {
        notifier(<ErrorNotification error={error} />, { variant: "error" });
      });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    validate();
  };

  return (
    <ThemeProvider theme={theme1}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Left Side - Branding */}
        <Grid container sx={{ height: '100vh' }}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(135deg, ${theme1.palette.primary.main} 0%, ${theme1.palette.primary.dark} 100%)`,
              position: 'relative',
              padding: 4,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                textAlign: 'center',
                color: theme1.palette.primary.contrastText,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="GV Productions"
                sx={{
                  height: { md: 120, lg: 150 },
                  width: 'auto',
                  objectFit: 'contain',
                  mb: 4,
                  filter: 'brightness(0) invert(1)',
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: theme1.palette.primary.contrastText,
                }}
              >
                GV PRODUCTIONS
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  color: alpha(theme1.palette.primary.contrastText, 0.9),
                  maxWidth: 400,
                  mx: 'auto',
                }}
              >
                Καλώς ήρθατε στο σύστημα διαχείρισης παραγωγών
              </Typography>
            </Box>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme1.palette.background.default,
              padding: { xs: 3, sm: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '550px',
              }}
            >
              {/* Logo for mobile */}
              <Box
                sx={{
                  mb: 4,
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="GV Productions"
                  sx={{
                    height: 60,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: theme1.palette.text.primary,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Σύνδεση
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mb: 4,
                  color: theme1.palette.text.secondary,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Εισάγετε τα στοιχεία σας για να συνδεθείτε
              </Typography>

              <Box
                component="form"
                onSubmit={handleFormSubmit}
                noValidate
                sx={{
                  width: '100%',
                }}
              >
                {/* Email Field */}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Διεύθυνση Email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme1.palette.primary.main,
                      },
                    },
                  }}
                />
                {/* Password Field */}
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Κωδικός Πρόσβασης"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme1.palette.primary.main,
                      },
                    },
                  }}
                />
                {/* Sign In Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: theme1.shadows[4],
                    '&:hover': {
                      boxShadow: theme1.shadows[6],
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Σύνδεση
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
